import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const GIFT_HERO =
  "https://huzyagtfxwapsccsksib.supabase.co/storage/v1/object/public/videos/assets/referral_welcome/pip_gift_wide.png";

export const PENDING_GIFT_KEY = "pixley_pending_gift_token";

const CONFETTI_COLORS = ["#0065F8", "#00CAFF", "#FFC940", "#FF5D73", "#3DDC84"];

function ConfettiBurst() {
  // Pure-CSS celebration: 28 pieces falling once. Respects reduced motion.
  const pieces = Array.from({ length: 28 }, (_, i) => i);
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      <style>{`
        @keyframes rp-fall {
          0%   { transform: translateY(-6vh) rotate(0deg); opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(105vh) rotate(660deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .rp-confetti { display: none; } }
      `}</style>
      {pieces.map((i) => {
        const left = (i * 37) % 100;
        const delay = ((i * 13) % 20) / 10;
        const duration = 3.4 + ((i * 7) % 18) / 10;
        const size = 7 + ((i * 5) % 8);
        const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
        const round = i % 3 === 0;
        return (
          <span
            key={i}
            className="rp-confetti"
            style={{
              position: "absolute",
              top: 0,
              left: `${left}%`,
              width: size,
              height: round ? size : size * 1.7,
              background: color,
              borderRadius: round ? "50%" : 3,
              animation: `rp-fall ${duration}s ${delay}s ease-in both`,
            }}
          />
        );
      })}
    </div>
  );
}

const PIP_ART = "https://huzyagtfxwapsccsksib.supabase.co/storage/v1/object/public/videos/assets/pip_email";

const PIXLEY_INTRO = [
  {
    title: "Drawings come alive",
    body: "Your child's doodle becomes a cartoon character with a voice of its own.",
    image: `${PIP_ART}/k_draw_one_thing_anim.gif`,
    alt: "Pip the panda drawing with a crayon",
  },
  {
    title: "Stories made for them",
    body: "Personalized episodes and storybooks starring the characters they created.",
    image: `${PIP_ART}/k_watch_a_sample_anim.gif`,
    alt: "Pip the panda pressing play on a story",
  },
  {
    title: "Play, learn, imagine",
    body: "Games, read-alongs, and character calls — a whole world, kid-safe.",
    image: `${PIP_ART}/k_weekly_play_report_anim.gif`,
    alt: "Pip the panda on a rainbow slide",
  },
];

function IntroCarousel() {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const touchX = React.useRef(null);

  React.useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % PIXLEY_INTRO.length), 3800);
    return () => clearInterval(id);
  }, [paused]);

  const go = (i) => {
    setIndex((i + PIXLEY_INTRO.length) % PIXLEY_INTRO.length);
    setPaused(true); // a person took the wheel; stop auto-advancing
  };

  return (
    <div
      style={{ margin: "18px 0 22px" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
      }}
    >
      <div style={{ overflow: "hidden", borderRadius: 20, boxShadow: "0px 4px 0px rgba(11,26,51,0.08), 0px 14px 30px rgba(11,26,51,0.10)" }}>
        <div
          style={{
            display: "flex",
            transform: `translateX(-${index * 100}%)`,
            transition: "transform 520ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {PIXLEY_INTRO.map((item) => (
            <div key={item.title} style={{ position: "relative", flex: "0 0 100%", aspectRatio: "16 / 9", background: "#BEE3FF" }}>
              <img
                src={item.image}
                alt={item.alt}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(11,26,51,0) 42%, rgba(11,26,51,0.78) 100%)" }} />
              <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "0 18px 14px", textAlign: "left" }}>
                <div style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 19 }}>{item.title}</div>
                <div style={{ ...FREDOKA, color: "rgba(255,255,255,0.92)", fontSize: 13, marginTop: 2, lineHeight: 1.45 }}>{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
        {PIXLEY_INTRO.map((item, i) => (
          <button
            key={item.title}
            onClick={() => go(i)}
            aria-label={`Show: ${item.title}`}
            style={{
              width: i === index ? 22 : 8,
              height: 8,
              borderRadius: 999,
              border: "none",
              padding: 0,
              cursor: "pointer",
              background: i === index ? "#0065F8" : "rgba(11,26,51,0.18)",
              transition: "all 300ms ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function RedeemPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  const { apiClient } = useAPI();

  const token = searchParams.get("token");
  const hasRedeemedRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [premiumUntil, setPremiumUntil] = useState("");
  const [gift, setGift] = useState(null); // /gifts/preview payload (may stay null)

  const durationLabel = (months) =>
    months === 1 ? "1 free month" : months ? `${months} free months` : "a free gift";

  // Remember the token the moment anyone lands here, so redemption survives
  // any detour (signup, onboarding) — App.js finishes the job if we never
  // make it back to this page.
  useEffect(() => {
    if (token) {
      try { localStorage.setItem(PENDING_GIFT_KEY, token); } catch {}
    }
  }, [token]);

  // Pre-auth: fetch what the gift is, purely to celebrate it properly.
  useEffect(() => {
    if (!token || !apiClient || isAuthenticated) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.request(`/gifts/preview?token=${encodeURIComponent(token)}`, { method: "GET" });
        if (alive && res?.status === "success" && res?.data) setGift(res.data);
      } catch {
        /* welcome page renders fine without details */
      }
    })();
    return () => { alive = false; };
  }, [token, apiClient, isAuthenticated]);

  // Authenticated: redeem right here (App.js skips /redeem to avoid racing us).
  useEffect(() => {
    if (loading || !token || !isAuthenticated) return;
    if (!apiClient || hasRedeemedRef.current) return;
    hasRedeemedRef.current = true;

    const redeemGift = async () => {
      setStatus("loading");
      setErrorMessage("");
      try {
        const authToken = apiClient.getAuthToken();
        const response = await fetch(`${apiClient.baseURL}/api/gifts/redeem`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ token }),
          credentials: "include",
        });
        let payload = {};
        try { payload = await response.json(); } catch { payload = {}; }

        if (!response.ok) {
          if (response.status === 400) setErrorMessage("This gift has already been redeemed.");
          else if (response.status === 403) setErrorMessage("This gift was sent to a different email address. Sign in with the email the gift was sent to.");
          else if (response.status === 404) setErrorMessage("Invalid gift token.");
          else setErrorMessage("Something went wrong. Please try again.");
          setStatus("error");
          return;
        }

        try { localStorage.removeItem(PENDING_GIFT_KEY); } catch {}
        const resolvedPremiumUntil = payload?.data?.premium_until || payload?.premium_until || "";
        setPremiumUntil(resolvedPremiumUntil);
        setStatus("success");
      } catch {
        setStatus("error");
        setErrorMessage("Something went wrong. Please try again.");
      }
    };

    redeemGift();
  }, [loading, token, isAuthenticated, apiClient]);

  const CARD = {
    background: "#FFFFFF",
    border: "2px solid rgba(11,26,51,0.06)",
    borderRadius: 28,
    boxShadow: "0px 5px 0px rgba(11,26,51,0.08), 0px 24px 64px rgba(11,26,51,0.10)",
  };

  const CTA = {
    ...FREDOKA,
    display: "inline-block",
    background: "#0065F8",
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: 17,
    border: "none",
    cursor: "pointer",
    padding: "15px 44px",
    borderRadius: 999,
    boxShadow: "0px 4px 0px #073B8E",
  };

  const claimGift = () => {
    const redeemPath = `/redeem?token=${encodeURIComponent(token || "")}`;
    navigate(`/auth?gift=1&redirect=${encodeURIComponent(redeemPath)}`);
  };

  // ---------------------------------------------------------------- render
  const missingToken = !token;
  const showWelcome = !loading && !isAuthenticated && !missingToken;

  return (
    <div
      className="min-h-screen relative px-4 sm:px-6 lg:px-8 pt-16 pb-12"
      style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)", fontFamily: "'Inter', 'Fredoka', sans-serif" }}
    >
      {showWelcome && <ConfettiBurst />}

      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-xl mx-auto">
        {missingToken && (
          <div className="text-center overflow-hidden p-9" style={CARD}>
            <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 24, margin: 0 }}>Invalid gift link</h2>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, marginTop: 10 }}>
              This link is missing its gift token. Double-check the link from your email.
            </p>
          </div>
        )}

        {/* ---------- Pre-auth: the gift welcome ---------- */}
        {showWelcome && (
          <div className="text-center overflow-hidden" style={CARD}>
            <img
              src={GIFT_HERO}
              alt="Pip the Pixley panda bursting out of a gift box"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
            <div className="p-8 pt-6">
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                A gift for your family
              </span>
              <h1 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: "clamp(1.7rem, 5vw, 2.4rem)", letterSpacing: "-0.01em", lineHeight: 1.1, margin: "10px 0 8px" }}>
                {gift?.sender_name
                  ? `${gift.sender_name} sent you ${durationLabel(gift?.duration_months)} of Pixley!`
                  : gift?.duration_months
                    ? `You've been gifted ${durationLabel(gift.duration_months)} of Pixley!`
                    : "You've got a Pixley gift!"}
              </h1>

              {gift?.personal_message ? (
                <div style={{ ...FREDOKA, background: "#FFF6DE", border: "1.5px solid #F5E3AE", borderRadius: 14, padding: "13px 18px", fontSize: 15, fontStyle: "italic", color: "#6B5A2A", margin: "14px 0" }}>
                  “{gift.personal_message}”
                </div>
              ) : (
                <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: "10px 0 14px" }}>
                  Someone thinks your family will love it here.
                </p>
              )}

              <IntroCarousel />

              {gift?.redeemed ? (
                <p style={{ ...FREDOKA, color: "#C77D08", fontSize: 14, fontWeight: 600 }}>
                  This gift has already been redeemed. If that was you, just sign in!
                </p>
              ) : null}

              <button onClick={claimGift} style={CTA}>
                Unlock my gift
              </button>
              <p style={{ ...FREDOKA, color: "#8A99B0", fontSize: 12.5, marginTop: 12 }}>
                Free account, takes about a minute.
                {gift?.recipient_email_masked ? ` Use ${gift.recipient_email_masked} — the email this gift was sent to.` : ""}
              </p>
            </div>
          </div>
        )}

        {/* ---------- Authenticated: redeem states ---------- */}
        {!showWelcome && !missingToken && (
          <div className="text-center overflow-hidden p-9" style={CARD}>
            {(loading || status === "loading" || status === "idle") && (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)", boxShadow: "0px 4px 0px #073B8E" }}>
                  <span style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.35)", borderTopColor: "#FFFFFF", animation: "rd-spin 1s linear infinite" }} />
                </div>
                <p style={{ ...FREDOKA, color: "#0B1A33", fontSize: 16, fontWeight: 700, margin: 0 }}>Unwrapping your gift…</p>
                <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>Hang tight, this only takes a moment.</p>
                <style>{`@keyframes rd-spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {status === "success" && (
              <div className="space-y-4">
                <ConfettiBurst />
                <img src={GIFT_HERO} alt="" style={{ display: "block", width: "100%", height: "auto", borderRadius: 18 }} />
                <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 26, margin: 0 }}>Your gift is unlocked!</h2>
                <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 }}>
                  Pixley Premium is active{premiumUntil ? ` until ${new Date(premiumUntil).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}` : ""}. Time to make something amazing.
                </p>
                <button onClick={() => navigate("/storyverse")} style={CTA}>Start creating</button>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-4">
                <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 24, margin: 0 }}>Hmm, that didn't work</h2>
                <p style={{ ...FREDOKA, color: "#C81E1E", fontSize: 14, margin: 0 }}>{errorMessage}</p>
                <button onClick={() => navigate("/storyverse")} style={{ ...CTA, background: "#5A6B85", boxShadow: "0px 4px 0px #3B4757" }}>
                  Go to Pixley
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
